import type { UniqueIdentifier } from "@dnd-kit/core";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import type { PageMetadata } from "@moment/api-collab/api-types";

import { useDocumentActor } from "~/state";

import { useEasel } from "../easel";
import { useCurrentDocument } from "../hooks/useCurrentDocument";
import { useEaselCurrentPage } from "../hooks/useEaselCurrentPage";
import { PageTree } from "./PageTree";
import { makePageTree } from "./makePageTree";
import type { TreeItems } from "./types";
import { findItemDeep, flattenTree } from "./utilities";

export const PageList: FC = () => {
	const { documentId } = useEasel();

	const documentActor = useDocumentActor(documentId);

	const reorderPages = useCallback(
		async (
			reorderInfo: {
				pageId: string;
				parentPageID: string | null;
			}[]
		) => {
			documentActor?.send({
				type: "pages.reordered",
				payload: {
					reorderInfo,
				},
			});
		},
		[documentActor]
	);

	// Hooks
	const activeDocument = useCurrentDocument();
	const activePage = useEaselCurrentPage();

	// Variables
	const allowPageDelete = useCallback(
		(page: PageMetadata) => {
			if (
				activeDocument === null ||
				page?.archived === true ||
				activeDocument?.pagesMetadata.filter((page) => page.archived === false).length === 1
			) {
				return false;
			}
			return true;
		},
		[activeDocument]
	);
	const filteredPages = useMemo(
		() => activeDocument?.pagesMetadata.filter((page) => !page.archived),
		[activeDocument]
	);
	const [localPages, setLocalPages] = useState<PageMetadata[]>(filteredPages ?? []);

	const [pageTree, setPageTree] = useState(() => makePageTree(localPages));
	useEffect(() => {
		setPageTree(makePageTree(localPages));
	}, [localPages]);

	function handleCollapse(id: UniqueIdentifier) {
		setPageTree((pageTree) => {
			const item = findItemDeep(pageTree, id);

			if (!item) {
				return pageTree;
			}

			// Just open the one item if it's collapsed.
			if (item?.collapsed === true) {
				item.collapsed = false;
				return [...pageTree];
			}

			// Else, recursively collapse any children.
			item.collapsed = true;
			const stack: TreeItems<PageMetadata> = [item];
			while (stack.length > 0) {
				const item = stack.pop();
				if (!item) {
					continue;
				}
				const children = item.children;
				for (const child of children) {
					child.collapsed = true;
					stack.push(child);
				}
			}
			return [...pageTree];
		});
	}

	// Functions

	const handleSetOrder = useCallback(
		async (items: TreeItems<PageMetadata>) => {
			if (!activePage || !activeDocument) {
				return;
			}

			const flattenedItems = flattenTree(items);
			const localPages = flattenedItems.map(({ data }) => data);
			const reorderInfo = flattenedItems.map((p) => ({
				pageId: p.data.id,
				parentPageID: p.data.parentPageID,
			}));

			// commit page reordering to the metadata server
			await reorderPages(reorderInfo);

			setLocalPages(localPages);
		},
		[activePage, activeDocument, reorderPages]
	);

	useEffect(() => {
		if (filteredPages === undefined) {
			return;
		}

		setLocalPages(filteredPages);
	}, [filteredPages]);

	if (!activeDocument || !localPages) {
		return;
	}

	return (
		<PageTree
			items={pageTree}
			setItemsOrder={handleSetOrder}
			collapseItem={handleCollapse}
			canvasId={activeDocument.id}
			indicator
			allowPageDelete={allowPageDelete}
			collapsible
		/>
	);
};
